import type { Method } from 'axios';
import axios from 'axios';

/**
 * APIs used by the Insights platform.
 */
export const enum Api {
    Auth = 'auth',
    Orgs = 'orgweb',
    Designs = 'designweb',
    Reports = 'reports',
    ReportsV2 = 'reporting',
    Cuts = 'cutweb',
    Systems = 'systemweb',
    CutState = 'cutstateweb',
    Tickets = 'tickets',
    Audit = 'audit',
}

/**
 * Get the API subdomain from environment variable.
 * Defaults to 'dev' if not set.
 */
const getApiSubdomain = (): string => {
    return import.meta.env.VITE_API_SUBDOMAIN || 'dev';
};

/**
 * Create the API URI for the configured environment.
 *
 * - In production builds: Uses relative paths (CloudFront proxies)
 * - In development: Uses full URLs based on VITE_API_SUBDOMAIN
 *
 * Set environment with: yarn env:switch
 */
export const createUri = (api: Api, path = '') => {
    if (!import.meta.env.DEV) {
        // Production build - use relative paths (CloudFront proxy)
        return path;
    }
    
    const subdomain = getApiSubdomain();
    return `https://${api}.${subdomain}.moutfittersync.net${path}`;
};

type ApiRequester<Input, Output> = (args: {
    body?: Input;
    token: string;
    params?: Record<string, any>;
}) => Promise<Output>;

type ApiFunction<Enum> = <Input, Output>(route: Enum, method: Method) => ApiRequester<Input, Output>;

/**
 * Composable API requests.
 */
export const createApi = <Enum>(uri: string): ApiFunction<Enum> => {
    const instance = axios.create({
        baseURL: uri,
    });

    return <Input, Output>(route: Enum, method: Method) => {
        return async ({
            body,
            token,
            params,
        }: {
            body?: Input;
            token: string;
            params?: Record<string, any>;
        }): Promise<Output> => {
            const { data } = await instance(route as string, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                data: body,
                params,
            });

            return data as Output;
        };
    };
};
